import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useOnboardingStore } from '../store/useOnboardingStore';
import { useAppTourStore } from '../store/useAppTourStore';
import { useUserDetails, useUserIdentity } from '../queries/user';
import { useCheckProfile } from '../queries/profile';
import { api } from '../services/api';
import { extractAccessToken, clearAuthData } from '../utils/authUtils';
import { updateSentryUserContext, clearSentryUserContext } from '../utils/sentryUserContext';
import NoAuthPage from '../pages/NoAuthPage';
import { getRedirectUrl } from '../utils/helpers';
import { Loader2 } from 'lucide-react';
import useErrorModalStore from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';
import { useGetPosts } from '../queries/post';
import useNotificationBannerStore from '../store/useNotificationBannerStore';

 
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { setAccessKey, setUser, isLoggingOut, isCheckingAuth, setIsCheckingAuth } = useAppStore();
  const { resetNotificationBannerStore } = useNotificationBannerStore();
  const {
    userEmail,
    flowState,
    setUserEmail,
    setFirstTimeLogin,
    resetOnboarding,
    setFlowState,
    completeOnboarding,
    setShowSuccessOverlay,
    markProfileReady,
  } = useOnboardingStore();
  const { completedTours, completeTour, resetTour } = useAppTourStore();
  const [authFailed, setAuthFailed] = useState(false);
  const [accessKey, setAccessKeyState] = useState(null);
  const [shouldFetchIdentity, setShouldFetchIdentity] = useState(false);
  const [shouldCheckPosts, setShouldCheckPosts] = useState(false);
  const [isEvaluatingOnboarding, setIsEvaluatingOnboarding] = useState(true);
  const { openErrorModal } = useErrorModalStore();
  // Handle logout
  useEffect(() => {
    if (isLoggingOut && !isCheckingAuth) {
      clearSentryUserContext();
      clearAuthData();
      window.location.href = getRedirectUrl('/login');
    }
  }, [isLoggingOut, isCheckingAuth]);

  // Update Sentry user context when user changes
  const { user } = useAppStore();
  useEffect(() => {
    updateSentryUserContext(user);
  }, [user]);

  // Initialize access key
  useEffect(() => {
    // Skip auth initialization if we're logging out
    if (isLoggingOut) {
      return;
    }

    setIsCheckingAuth(true);
    setAuthFailed(false);

    const key = extractAccessToken();
    if (!key) {
      setAuthFailed(true);
      setIsCheckingAuth(false);
      setAccessKey('');
      api.setAccessKey('');
      return;
    }

    api.setAccessKey(key);
    setAccessKey(key);
    setAccessKeyState(key);
  }, [setAccessKey, setIsCheckingAuth, isLoggingOut]);

  // Fetch user details
  const {
    data: userDetails,
    isLoading: userLoading,
    isError: userError,
  } = useUserDetails(
    !!accessKey,
    (data) => {
      setUser(data);
      const isValidUser = data && !data.error && data.is_verified;
      setAuthFailed(!isValidUser);
      setIsCheckingAuth(false);

      if (!isValidUser) {
        setAccessKey('');
      } else {
        setShouldFetchIdentity(true);
      }
    },
    () => {
      setAuthFailed(true);
      setIsCheckingAuth(false);
      setAccessKey('');
    }
  );

  // Fetch user identity
  const { data: userIdentity, isLoading: identityLoading } = useUserIdentity(
    !!accessKey && shouldFetchIdentity
  );

  // Check profile status
  const { data: profileData, isLoading: profileLoading } = useCheckProfile(
    !!accessKey,
    (data) => {
      // Just enable posts checking if profile exists
      if (data?.status === 'created') {
        setShouldCheckPosts(true);
      }
      if (data?.status === 'failed') {
        openErrorModal(getErrorModalMessage('profile_creation_failed'), 'profile_check_error', {
          title: 'Start Over',
          action: () => {
            resetOnboarding();
          },
        });
      }
    },
    () => {
      // Profile check failed - will be handled in evaluation effect
    }
  );

  // Fetch posts data
  const { data: postsData, isLoading: isLoadingPosts } = useGetPosts(shouldCheckPosts, null, null);

  // Check if user has posts with valid status (pending, published, or approved)
  const hasPendingPost = useMemo(() => {
    return (
      postsData?.some((post) => ['pending', 'published', 'approved'].includes(post.status)) ?? false
    );
  }, [postsData]);

  // Handle email changes
  useEffect(() => {
    if (!userIdentity?.email) return;

    const currentEmail = userIdentity.email;
    const emailChanged = userEmail !== currentEmail;
    setUser({ ...userDetails, identity: { ...userIdentity } });
    if (emailChanged) {
      setUserEmail(currentEmail);
      // Trigger re-evaluation by resetting flowState
      if (flowState !== null) {
        setFlowState(null);
        setIsEvaluatingOnboarding(true);
      }
    }
     
  }, [userIdentity, userEmail, flowState, setUserEmail, setFlowState]);

  // Evaluate onboarding state - make atomic decision
  useEffect(() => {
    // Only skip evaluation if we're in a stable final state
    // Allow re-evaluation for intermediate states like 'analyzing' or null
    const isFinalState = flowState === 'completed' || flowState === 'profile_ready';
    const isActiveOnboarding =
      ['analyzing', 'generating_post'].includes(flowState) &&
      location.pathname.includes('/onboarding');

    if (isFinalState || isActiveOnboarding) {
      setIsEvaluatingOnboarding(false);
      return;
    }

    // Wait for all necessary data
    if (!userIdentity?.email || profileLoading || identityLoading) {
      return;
    }

    const profileCreated = profileData?.status === 'created';
    const hasProfileError = !profileData;

    // CASE 1: No profile or error -> go to onboarding
    if (hasProfileError || !profileCreated) {
      resetOnboarding();
      setIsEvaluatingOnboarding(false);
      return;
    }

    // CASE 2: Profile exists - check posts
    if (!shouldCheckPosts) {
      // Profile created but haven't enabled posts check yet - wait
      return;
    }

    // Wait for posts to be loaded before deciding
    if (isLoadingPosts) {
      return;
    }

    const hasPosts = postsData?.length > 0;

    // CASE 2a: Profile exists, no posts -> mark profile ready
    if (!hasPosts) {
      markProfileReady();
      setFirstTimeLogin(false);
      setIsEvaluatingOnboarding(false);
      return;
    }

    // CASE 2b: Profile exists and has posts with valid status -> complete onboarding
    if (hasPendingPost) {
      completeOnboarding();
      setShowSuccessOverlay(false); // Don't show overlay for returning users
      setFirstTimeLogin(false);

      // Mark tour as completed for returning users to prevent auto-start
      if (!completedTours.includes('default')) {
        completeTour('default');
      }

      setIsEvaluatingOnboarding(false);
      return;
    }

    // Default: if we have posts but none with valid status, go to profile_ready
    markProfileReady();
    setFirstTimeLogin(false);
    setIsEvaluatingOnboarding(false);
     
  }, [
    flowState,
    shouldCheckPosts,
    isLoadingPosts,
    postsData,
    hasPendingPost,
    profileData,
    userIdentity,
    profileLoading,
    identityLoading,
    location.pathname,
    resetOnboarding,
    markProfileReady,
    completeOnboarding,
    setFlowState,
    setFirstTimeLogin,
    setShowSuccessOverlay,
  ]);

  // Handle errors and loading completion
  useEffect(() => {
    if (!accessKey) return;

    if (userError) {
      setAuthFailed(true);
      setIsCheckingAuth(false);
      setAccessKey('');
    } else if (!userLoading && !identityLoading && !profileLoading && userDetails && userIdentity) {
      setIsCheckingAuth(false);
    }
  }, [
    accessKey,
    userError,
    userLoading,
    identityLoading,
    profileLoading,
    userDetails,
    userIdentity,
    setIsCheckingAuth,
    setAccessKey,
  ]);

  // Determine if we need to redirect based on onboarding state
  const isOnboardingInProgress = [
    'not_started',
    'analyzing',
    'profile_ready',
    'generating_post',
    'failed',
  ].includes(flowState);

  const shouldRedirectToOnboarding =
    flowState !== null &&
    !isEvaluatingOnboarding &&
    isOnboardingInProgress &&
    !location.pathname.includes('/onboarding');

  const shouldRedirectToDashboard =
    flowState !== null &&
    !isEvaluatingOnboarding &&
    flowState === 'completed' &&
    location.pathname.includes('/onboarding');

  // Compute loading state - show loading while flowState is null (checking) or during initial checks
  const isLoading =
    (isCheckingAuth ||
      userLoading ||
      identityLoading ||
      profileLoading ||
      flowState === null ||
      isEvaluatingOnboarding) &&
    !authFailed;

  useEffect(() => {
    if (shouldRedirectToOnboarding) {
      resetTour();
      resetNotificationBannerStore();
    }
    if (shouldRedirectToDashboard) {
      resetNotificationBannerStore();
    }
     
  }, [shouldRedirectToOnboarding, shouldRedirectToDashboard]);

  // Show loading - this prevents any route from rendering until we know where to go
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center' style={{ background: 'var(--bg-base)' }}>
        <div className='text-center'>
          <Loader2 className='w-16 h-16 animate-spin mx-auto mb-4' style={{ color: 'var(--brand)' }} />
        </div>
      </div>
    );
  }

  // Show auth failed
  if (authFailed) {
    return <NoAuthPage />;
  }

  // Handle redirects - these happen BEFORE rendering children (Outlet)
  // This prevents any flash of onboarding page before redirect
  if (shouldRedirectToOnboarding) {
    return <Navigate to='/onboarding' replace />;
  }

  if (shouldRedirectToDashboard) {
    return <Navigate to='/dashboard' replace />;
  }

  // All checks passed, render the route content
  return children;
};

export default ProtectedRoute;
